import { Text as T, useTranslator } from '@eo-locale/preact';
import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import { Review, ReviewSorting, VerificationStatus } from 'types/review';
import { ExtendedReviewsTabWidgetSettings } from 'types/settings';
import { WidgetRequest } from 'types/widget';
import { useReviews } from 'utilities/api';
import { useDebounce } from 'utilities/use-debounce';
import { useSorting } from 'utilities/use-sorting';
import { SortingKeyToLabel } from 'widgets/ReviewsWidget/types/sorting';

import { ActionList } from 'components/ActionList';
import { Button } from 'components/Button';
import { Card } from 'components/Card';
import { Modal } from 'components/Modal';
import { useModal } from 'components/ModalProvider';
import { useSettings } from 'components/SettingsProvider';
import { Spinner } from 'components/Spinner';
import { Stack } from 'components/Stack';
import { Text } from 'components/Text';

import { ReviewCard } from './components/ReviewCard';
import { SkeletonContent } from './components/SkeletonContent';

import styles from '../../ReviewsTabWidget.module.scss';

export function ReviewsModal() {
  const { translate } = useTranslator();
  const {
    product_ids,
    filter_by_min_rating,
    filter_by_verified,
    filter_with_images,
    reviews_per_page,
    utils: { locale },
  } = useSettings<ExtendedReviewsTabWidgetSettings>();

  const [reviewsData, setReviewsData] = useState<Review[]>([]);

  const { open, closeModal } = useModal();

  const [dataUpdatedTime, setDataUpdatedTime] = useState(0);

  const { sortBy, sortingOptions, changeSorting } = useSorting();

  const [page, setPage] = useState(1);

  const request = useMemo<WidgetRequest>(
    () => ({
      components: {
        images: true,
        rating_details: true,
      },
      filters: {
        has_images: filter_with_images ? true : null,
        product_ids,
        rating_from: filter_by_min_rating,
        rating_to: 5,
        verified: filter_by_verified ? VerificationStatus.VERIFIED : null,
      },
      locale,
      count: reviews_per_page,
      page,
      sort: sortBy,
    }),
    [
      product_ids,
      page,
      filter_by_verified,
      filter_by_min_rating,
      filter_with_images,
      reviews_per_page,
      sortBy,
      locale,
    ],
  );

  const debouncedRequest = useDebounce<WidgetRequest>(request, 250);

  useEffect(() => {
    setPage(1);
  }, [sortBy]);

  const { dataUpdatedAt, reviews, meta, isLoading, isFetching } =
    useReviews(debouncedRequest);

  const handleNextPage = useCallback(() => setPage((page) => page + 1), []);

  useEffect(() => {
    if (dataUpdatedAt !== dataUpdatedTime) {
      setDataUpdatedTime(dataUpdatedAt);

      if (page === 1) setReviewsData(reviews);
      else setReviewsData((prevReviews) => [...prevReviews, ...reviews]);
    }
  }, [dataUpdatedTime, dataUpdatedAt, reviews, page]);

  const totalPages = Math.ceil(meta.total / meta.count);

  const activator = reviews.length > 0 && (
    <Button rounded disabled={isLoading || isFetching} iconPlacement="right">
      <Stack alignment="baseline" spacing="extraTight">
        <Text size="small" as="span" variation="subdued">
          <T id="reviews_list_sort_by_label" defaultMessage="Sort by" />:
        </Text>
        <Text as="span">
          {translate(SortingKeyToLabel[sortBy], {
            defaultMessage: SortingKeyToLabel[sortBy],
          })}
        </Text>
      </Stack>
    </Button>
  );

  const reviewsMarkup =
    reviews.length > 0 ? (
      reviewsData.map((review) => (
        <ReviewCard key={review.id} review={review} />
      ))
    ) : (
      <Stack>
        <Stack.Item fill>
          <Card sectioned subdued rounded={false}>
            <Stack>
              <Text>
                <T
                  id="reviews_list_no_reviews_message"
                  defaultMessage="No reviews yet."
                />
              </Text>
            </Stack>
          </Card>
        </Stack.Item>
      </Stack>
    );

  const spinnerMarkup = isFetching && (
    <div className={styles.LoadingOverlay}>
      <Spinner />
    </div>
  );

  const contentMarkup = isLoading ? (
    <SkeletonContent />
  ) : (
    <div className={styles.BodyWrapper}>
      <Stack vertical>
        <Stack distribution="trailing">
          <ActionList
            key="sorting"
            selected={sortBy}
            title={translate('reviews_list_sort_by_label', {
              defaultMessage: 'Sort by',
            })}
            options={sortingOptions}
            activator={activator}
            onChange={(value) => changeSorting(value as ReviewSorting)}
          />
        </Stack>
        {reviewsMarkup}
        {reviews.length > 0 && (
          <Stack distribution="trailing">
            <Button
              rounded
              primary
              loading={isFetching}
              disabled={totalPages <= page}
              onClick={handleNextPage}
            >
              <span>
                <T
                  id="reviews_list_show_more_reviews_button"
                  defaultMessage="Show more reviews"
                />
              </span>
            </Button>
          </Stack>
        )}
      </Stack>
      {spinnerMarkup}
    </div>
  );

  return (
    <Modal
      large
      title={translate('widget_reviews_tab_popup_title', {
        defaultMessage: 'Reviews from our customers',
      })}
      open={open}
      onClose={closeModal}
    >
      {contentMarkup}
    </Modal>
  );
}
